'use client';

import { useTranslations } from 'next-intl';
import React, { useRef } from 'react';
import styles from './CartOverview.module.css';
import { useSearchParams } from 'next/navigation';
import { useHomeContext } from '../HomeContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { DotSpinner, useCartStore } from 'components/shared';
import { Charger, Return } from 'assets/icons';
import {
  HomePriceStriked,
  productMap_home,
} from 'content/products/uhHome';
import { Section } from './styled';
import { useFormatPrice, CurrencySymbol } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';

// Card Styling
const Card = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.card} {...props}>
    {children}
  </div>
);

// Content Section
const Content = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.content}>{children}</div>
);

const LineItem = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.lineItem} {...props}>
    {children}
  </div>
);

const LineItemLabel = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.lineItemLabel}>{children}</div>
);

const LineItemPrice = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.lineItemPrice}>{children}</div>
);

export const Divider = ({ pixelPadding }: { pixelPadding?: string }) => (
  <div
    className={styles.divider}
    style={
      pixelPadding
        ? ({ ['--divider-margin']: `${pixelPadding} 0` } as React.CSSProperties)
        : undefined
    }
  />
);

const TotalContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.totalContainer}>{children}</div>
);

// Tax and Shipping Info
const TaxInfo = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={className ? `${styles.taxInfo} ${className}` : styles.taxInfo}
    {...props}
  >
    {children}
  </p>
);

interface ButtonWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  $isButtonDisabled: boolean;
}

export const ButtonWrapper = ({
  $isButtonDisabled,
  children,
  ...props
}: ButtonWrapperProps) => (
  <div
    className={styles.buttonWrapper}
    data-button-disabled={$isButtonDisabled ? 'true' : 'false'}
    {...props}
  >
    {children}
  </div>
);

// Benefits Section
const BenefitsSection = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.benefitsSection}>{children}</div>
);

// Benefit Item
const BenefitItem = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={
      className ? `${styles.benefitItem} ${className}` : styles.benefitItem
    }
  >
    {children}
  </div>
);

// Disclaimer Text
const Disclaimer = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.disclaimer}>{children}</p>
);

// Main Component
export const CartSummary: React.FC<{
  defaultOfferText?: string;
  defaultOfferDiscount?: number;
  defaultStrikedPrice?: boolean;
  handleCheckout: () => void;
}> = ({
  defaultOfferText,
  defaultOfferDiscount,
  defaultStrikedPrice,
  handleCheckout,
}) => {
  const cartSummaryRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('ring');
  const searchParams = useSearchParams();
  const getCampaignForProduct = useCartStore((s) => s.getCampaignForProduct);
  const formatPriceWithComma = useFormatPrice();
  const homeCampaign = getCampaignForProduct('home');

  const {
    defaultCurrency,
    store,
    uhx,
    uhxCart,
    uhxPrice,
    price,
    dueEffectivePrice,
    affiliateDiscount,
    referral,
    coverageRef,
    effectiveDiscount,
    birthdayDiscount,
    isLoading,
  } = useHomeContext();

  // Get currency symbol safely (same as Ring buy section)
  const currencySymbol =
    CurrencySymbol[defaultCurrency.toLowerCase() as Currency];

  // New method for handling wrapper clicks
  const handleWrapperClick = () => {
    // Only do something if the button is disabled
    if (!(uhx || uhxCart !== null)) {
      console.log('OrderSummary: Wrapper clicked, scrolling to UHX input');
      if (coverageRef && coverageRef.current) {
        coverageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  return (
    <Section
      show={uhx || uhxCart !== null}
      ref={cartSummaryRef}
      childClass="cart-summary"
    >
      <Card id="order-summary-card">
        <h4>{t('ring.productSelection.cartSummary.head')}</h4>

        <Content>
          <h5>Ultrahuman Home</h5>

          <LineItem>
            <LineItemLabel>Home</LineItemLabel>
            <LineItemPrice>
              <>
                {currencySymbol}
                {defaultStrikedPrice && !(affiliateDiscount || referral)
                  ? formatPriceWithComma(
                      HomePriceStriked[store as ShopifyStore],
                    )
                  : formatPriceWithComma(price)}
              </>
            </LineItemPrice>
          </LineItem>
          {effectiveDiscount.type === 'homePromoDiscount' && (
            <>
              <LineItem style={{ marginTop: '4px' }}>
                <LineItemLabel>
                  {homeCampaign?.title || 'Special'} Offer Applied
                </LineItemLabel>
                <LineItemPrice>
                  -{currencySymbol}
                  {formatPriceWithComma(effectiveDiscount.amount)}
                </LineItemPrice>
              </LineItem>
            </>
          )}

          {uhxCart && (
            <>
              <Divider pixelPadding={'16px'} />
              <LineItem>
                <LineItemLabel>
                  {uhxCart === 'Year2'
                    ? `${t('ring.UltrahumanX')} - 2 years`
                    : `${t('ring.UltrahumanX')} - 1 year`}
                </LineItemLabel>
                <LineItemPrice>
                  {uhxPrice === 0 ? (
                    <>
                      {/* FREE */}
                      {t('ring.FREE')}
                    </>
                  ) : (
                    <>
                      +{currencySymbol}
                      {formatPriceWithComma(uhxPrice)}
                    </>
                  )}
                </LineItemPrice>
              </LineItem>
            </>
          )}

          {affiliateDiscount > 0 && (
            <>
              <Divider pixelPadding={'16px'} />
              <LineItem>
                <LineItemLabel>
                  {t('ring.productSelection.cartSummary.priceBreakup.b')}
                </LineItemLabel>
                <LineItemPrice>
                  <>
                    -{currencySymbol}
                    {formatPriceWithComma(affiliateDiscount)}
                  </>
                </LineItemPrice>
              </LineItem>
            </>
          )}

          {referral && (
            <>
              <Divider pixelPadding={'16px'} />
              <LineItem>
                <LineItemLabel>
                  {t('ring.productSelection.cartSummary.priceBreakup.b')}
                </LineItemLabel>
                <LineItemPrice>
                  <>
                    -{currencySymbol}
                    {formatPriceWithComma(referral.discount)}
                  </>
                </LineItemPrice>
              </LineItem>
            </>
          )}
          {effectiveDiscount.type === 'birthday' && birthdayDiscount > 0 && (
            <>
              <Divider pixelPadding={'16px'} />
              <LineItem>
                <LineItemLabel>
                  {(() => {
                    const discountCode = searchParams?.get('discount') as string;
                    const percentage = discountCode
                      ? parseInt(discountCode.slice(-2)) || 15
                      : 15;
                    return `Birthday discount (${percentage}%)`;
                  })()}
                </LineItemLabel>
                <LineItemPrice>
                  <>
                    -{currencySymbol}
                    {formatPriceWithComma(birthdayDiscount)}
                  </>
                </LineItemPrice>
              </LineItem>
            </>
          )}

          {!(
            !(store === ShopifyStore.GB || store === ShopifyStore.EU) &&
            affiliateDiscount
          ) &&
            defaultOfferText &&
            defaultOfferDiscount && (
              <>
                <Divider pixelPadding={'16px'} />
                <LineItem>
                  <LineItemLabel>{defaultOfferText} offer </LineItemLabel>
                  <LineItemPrice>
                    <>
                      -{currencySymbol}
                      {formatPriceWithComma(defaultOfferDiscount)}
                    </>
                  </LineItemPrice>
                </LineItem>
              </>
            )}
        </Content>
        <Divider pixelPadding={'16px'} />
        <TotalContainer>
          <span>{t('ring.TOTAL')}</span>
          <LineItemPrice>
            <>
              {currencySymbol}
              {formatPriceWithComma(dueEffectivePrice)}
            </>
          </LineItemPrice>
        </TotalContainer>
        <Divider pixelPadding={'16px'} />

        {store &&
        productMap_home?.taxesInclusion[store as ShopifyStore] ? null : (
          <TaxInfo style={{ marginBottom: '25px' }}>
            *Shipping fees and taxes will be calculated at checkout.
          </TaxInfo>
        )}
        {/* 
          {shippingEstimate && (
            <TaxInfo className="shipping-estimate">{shippingEstimate}</TaxInfo>
          )} */}

        <ButtonWrapper
          onClick={handleWrapperClick}
          $isButtonDisabled={isLoading || !(uhx || uhxCart !== null)}
        >
          <PrimaryButton
            data-buttontype="add to cart"
            data-product="Ultrahuman Home"
            data-variantid={
              defaultCurrency
                ? productMap_home?.variantId[store as ShopifyStore]
                : ''
            }
            onClick={handleCheckout}
            disabled={isLoading || !(uhx || uhxCart !== null)}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <DotSpinner color="#000000" />
            ) : (
              t('ring.productSelection.cartSummary.addToCart')
            )}
          </PrimaryButton>
        </ButtonWrapper>

        {/* <CheckoutButton
          className="checkout"
          disabled={!(uhx || uhxCart !== null)}
          data-buttontype="add to cart"
          data-product="Ultrahuman Home"
          data-variantid={
            defaultCurrency
              ? productMap_home?.variantId[store as ShopifyStore]
              : ''
          }
          onClick={handleCheckout}
        >
          {t('ring.productSelection.cartSummary.addToCart')}
        </CheckoutButton> */}

        {/* <ShippingInfo>
            <p className="free-delivery">
              {`No cost EMI starting at ${getCurrencySymbolElement(defaultCurrency)}${" "}${
                Number(dueEffectivePrice/12).toFixed(2)
              }/month. Free Delivery`}
            </p>
          </ShippingInfo> */}
        {/* </TotalSection> */}

        <BenefitsSection>
          <BenefitItem className="charger benefit">
            <Charger />
            <div>
              <p>Charging adapter and cable included</p>
              {/* <p>
                {t('ring.INCLUDED')}
              </p> */}
            </div>
          </BenefitItem>

          <BenefitItem className="return benefit">
            <Return />
            <p>Hassle-free 30-day returns and replacements</p>
          </BenefitItem>
        </BenefitsSection>

        {store &&
          ![
            ShopifyStore.IN,
            ShopifyStore.AE,
            ShopifyStore.GB,
            ShopifyStore.EU,
          ].includes(store as ShopifyStore) && (
            <Disclaimer>
              {t('ring.productSelection.cartSummary.benefits.disclaimer')}
            </Disclaimer>
          )}

        {/* <PlantTreesBanner /> */}
      </Card>
    </Section>
  );
};

export default CartSummary;
